/**
 * Repeatable establishment contacts grid (function, email, phone).
 */

import * as React from 'react';
import { Plus, Trash2, Mail } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { DynamicFunctionSelect } from '@/components/common/DynamicFunctionSelect';
import { cn } from '@/lib/utils';
import type { EstablishmentContact } from '@/types/establishment.types';
import { useDefaultPhoneCountry } from '@/utils/user-contact-utils';
import { getPhoneFieldError, getPhoneFormat } from '@/utils/phone-formats-utils';

/** Contact row with a stable local id for React keys. */
interface ContactWithId extends EstablishmentContact {
  _tempId: string;
}

/** Props for {@link ContactsGrid}. */
interface ContactsGridProps {
  value?: EstablishmentContact[];
  onChange?: (contacts: EstablishmentContact[]) => void;
  disabled?: boolean;
  className?: string;
  /** Show phone errors on load (e.g. invalid API data). */
  showPhoneValidationOnLoad?: boolean;
}

/** Reuse an existing `_tempId` or mint one so React keys stay stable. */
const makeContactWithId = (contact: EstablishmentContact): ContactWithId => ({
  ...contact,
  _tempId: (contact as ContactWithId)._tempId || `contact-${crypto.randomUUID()}`,
});

/** Empty contact row with a fresh local id. */
const makeDefaultContact = (): ContactWithId => ({
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  fonction: undefined,
  _tempId: `contact-${crypto.randomUUID()}`,
});

/** Editable grid of named contacts (email / phone). */
export const ContactsGrid: React.FC<ContactsGridProps> = ({
  value = [],
  onChange,
  disabled = false,
  className,
  showPhoneValidationOnLoad = true,
}) => {
  const defaultCountry = useDefaultPhoneCountry();
  const phoneFormat = getPhoneFormat(defaultCountry);

  const [phoneTouched, setPhoneTouched] = React.useState<Record<string, boolean>>({});

  const [contacts, setContacts] = React.useState<ContactWithId[]>(() => {
    const initial = value || [];
    return initial.length > 0 ? initial.map(makeContactWithId) : [makeDefaultContact()];
  });

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const external = value || [];
    const hasExternalIds = external.length > 0 && !!(external[0] as ContactWithId)._tempId;
    if (!hasExternalIds) {
      setContacts(external.length > 0 ? external.map(makeContactWithId) : [makeDefaultContact()]);
    }
  }, [value]);

  /** Country-aware format error; empty phone is not treated as invalid. */
  const getPhoneError = React.useCallback(
    (phone: string | undefined) => {
      if (!phone?.trim()) return undefined;
      return getPhoneFieldError(phone, defaultCountry);
    },
    [defaultCountry],
  );

  /** Surface a format error on load (invalid API data) or after the field was blurred. */
  const shouldShowPhoneError = React.useCallback(
    (tempId: string, phone: string | undefined) => {
      const err = getPhoneError(phone);
      if (!err) return false;
      return showPhoneValidationOnLoad || phoneTouched[tempId];
    },
    [getPhoneError, showPhoneValidationOnLoad, phoneTouched],
  );

  /** Append an empty row and notify the parent. */
  const addContact = () => {
    const updated = [...contacts, makeDefaultContact()];
    setContacts(updated);
    onChange?.(updated);
  };

  /** Drop the row by local id; refuse to remove the last remaining contact. */
  const removeContact = (tempId: string) => {
    if (contacts.length === 1) return;
    const updated = contacts.filter((c) => c._tempId !== tempId);
    setContacts(updated);
    onChange?.(updated);
  };

  /** Patch one contact field and notify the parent. */
  const updateContact = (
    tempId: string,
    field: keyof EstablishmentContact,
    val: string | undefined,
  ) => {
    const updated = contacts.map((c) => (c._tempId === tempId ? { ...c, [field]: val } : c));
    setContacts(updated);
    onChange?.(updated);
  };

  /** Record blur so format errors can surface after the user interacted. */
  const markPhoneTouched = (tempId: string) => {
    setPhoneTouched((prev) => (prev[tempId] ? prev : { ...prev, [tempId]: true }));
  };

  const defaultPhoneHelper = phoneFormat
    ? `Format attendu : ${phoneFormat.example} (${phoneFormat.minLength} chiffres)`
    : undefined;

  return (
    <div className={cn('space-y-4', className)}>
      <div className="space-y-4">
        {contacts.map((contact, index) => {
          const phoneError = getPhoneError(contact.contactPhone);
          const showPhoneError = shouldShowPhoneError(contact._tempId, contact.contactPhone);
          let phoneState: 'success' | 'error' | 'default' = 'default';
          if (contact.contactPhone?.trim() && !phoneError) phoneState = 'success';
          else if (showPhoneError) phoneState = 'error';

          return (
            <div key={contact._tempId}>
              {contacts.length > 1 && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    Contact {index + 1}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeContact(contact._tempId)}
                    disabled={disabled}
                    icon={Trash2}
                    className="text-error hover:text-error hover:bg-error-light"
                  >
                    Supprimer
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom du contact"
                  value={contact.contactName || ''}
                  onChange={(e) => {
                    updateContact(contact._tempId, 'contactName', e.target.value);
                  }}
                  disabled={disabled}
                  placeholder="Ex: Marie Dupont"
                  maxLength={255}
                />

                <DynamicFunctionSelect
                  label="Fonction"
                  value={contact.fonction || ''}
                  onChange={(val) => {
                    updateContact(contact._tempId, 'fonction', val);
                  }}
                  disabled={disabled}
                />

                <Input
                  label="Email"
                  type="email"
                  value={contact.contactEmail || ''}
                  onChange={(e) => {
                    updateContact(contact._tempId, 'contactEmail', e.target.value);
                  }}
                  disabled={disabled}
                  placeholder="Ex: cpe@college.fr"
                  maxLength={255}
                  icon={Mail}
                />

                <PhoneInput
                  label="Téléphone"
                  value={contact.contactPhone || ''}
                  onChange={(e) => {
                    updateContact(contact._tempId, 'contactPhone', e.target.value);
                  }}
                  onBlur={() => markPhoneTouched(contact._tempId)}
                  countryCode={defaultCountry}
                  disabled={disabled}
                  state={phoneState}
                  error={showPhoneError ? phoneError : undefined}
                  helperText={!showPhoneError ? defaultPhoneHelper : undefined}
                />
              </div>
              {index < contacts.length - 1 && (
                <div className="border-t border-[color-mix(in_srgb,var(--foreground)_10%,transparent)] mt-4" />
              )}
            </div>
          );
        })}
      </div>

      <Button
        variant="outline"
        size="sm"
        icon={Plus}
        iconPosition="left"
        onClick={addContact}
        disabled={disabled}
        className="w-full mt-4"
      >
        Ajouter un contact
      </Button>
    </div>
  );
};
